import { DOCUMENT } from '@angular/common';
import { APP_INITIALIZER, EnvironmentProviders, Inject, makeEnvironmentProviders } from '@angular/core';
import { ENV_BASE, EnvBase } from '@bcf-v2-configs/env-base';
import { extractCookies } from '@bcf-v2-utilities/cookies-provider/utils';
import { extractRegistrationBannerIdFromFragment } from '@bcf-vanilla-ts-v1-logic-worker-modals/services/registration-banner-id';
import { AffiliateDataMemory } from './affiliate-data-memory';

async function updateAffiliateDataFromLink(
  affiliateDataMemory: AffiliateDataMemory,
  btag: string | undefined,
  bannerId: string | undefined
): Promise<void> {
  const isBtagAccepted: boolean = await affiliateDataMemory.updateDataRaw(btag);
  if (isBtagAccepted) {
    await affiliateDataMemory.updateRegistrationBannerId(bannerId);
  }
}

// For a banner link opened without a page reload, where the app initializer does not run again.
export function updateRegistrationBannerIdFromLink(
  affiliateDataMemory: AffiliateDataMemory,
  search: string,
  bannerId: string
): Promise<void> {
  const searchParams: URLSearchParams = new URLSearchParams(search);
  if (searchParams.has('btag')) {
    return updateAffiliateDataFromLink(affiliateDataMemory, searchParams.get('btag') ?? undefined, bannerId);
  }
  return affiliateDataMemory.updateRegistrationBannerId(bannerId);
}

export function provideAffiliateData(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: AffiliateDataMemory,
      useClass: AffiliateDataMemory
    },
    {
      provide: APP_INITIALIZER,
      useFactory:
        (document: Document, envBase: EnvBase, affiliateDataMemory: AffiliateDataMemory) =>
        (): Promise<void> | undefined => {
          const searchParams: URLSearchParams = new URLSearchParams(document.location.search);
          const extractedCookies: Record<string, string> = extractCookies(document.cookie);
          const btagCookieName: string | undefined = envBase.cookiesMap?.btag;
          const bannerId: string | undefined = extractRegistrationBannerIdFromFragment(document.location.hash);

          if (searchParams.has('btag')) {
            return updateAffiliateDataFromLink(
              affiliateDataMemory,
              searchParams.get('btag') ?? undefined,
              bannerId
            ).catch(() => undefined);
          }

          if (btagCookieName && extractedCookies[btagCookieName]) {
            affiliateDataMemory.updateDataRaw(extractedCookies[btagCookieName]);
          }

          if (bannerId) {
            return affiliateDataMemory.updateRegistrationBannerId(bannerId).catch(() => undefined);
          }
          return undefined;
        },
      deps: [[new Inject(DOCUMENT)], [new Inject(ENV_BASE)], AffiliateDataMemory],
      multi: true
    }
  ]);
}
